import {
	data,
	Link,
	NavLink,
	Outlet,
	redirect,
	useRouteLoaderData,
} from 'react-router'
import { OyAvatar } from '#app/components/oy/oy-avatar.tsx'
import {
	OyTabBar,
	TabMark,
	type OyTabItem,
} from '#app/components/oy/oy-nav.tsx'
import { OyPreviewBanner } from '#app/components/oy/oy-preview.tsx'
import { StalClosedDoor } from '#app/components/oy/oy-stal.tsx'
import { OyToastProvider } from '#app/components/oy/oy-toast.tsx'
import {
	ElseStalChrome,
	ElseTrayProvider,
} from '#app/components/stal/stal-else.tsx'
import {
	InstructeurChrome,
	InstructeurTrayProvider,
} from '#app/components/stal/stal-instructeur.tsx'
import { StalTrayProvider } from '#app/components/stal/stal-trays.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import oyElseDagStylesheetUrl from '#app/styles/oy-else-dag.css?url'
import oyStalStylesheetUrl from '#app/styles/oy-stal.css?url'
import { resolveStalViewer } from '#app/utils/stal-access.server.ts'
import {
	loadElseShell,
	STAL_ELSE_PATH,
	type ElseShell,
} from '#app/utils/stal-else.server.ts'
import {
	loadInstructeurShell,
	STAL_INSTRUCTEUR_PATH,
	type InstructeurShell,
} from '#app/utils/stal-instructeur.server.ts'
import { loadStalShell, type StalShell } from '#app/utils/stal-shell.server.ts'
import { STAL_LOGIN_PATH } from '#app/utils/stal-viewer.server.ts'
import { type Route } from './+types/_layout.ts'

/**
 * Herontwerp P6 — the Stal-app shell (StalApp.dc.html header + tab bar).
 *
 * The URL /stal/* is one layout: stal-green tile + the stable's name, the
 * viewer's avatar (→ Stal tab), five emoji tabs with the warm dot on Lessen
 * (a lesson waits for an answer) and Overleg (unread), the toast region
 * and the tray slot. The session is the `oyka_stal` cookie
 * (stal-access.server.ts); no viewer → /stal/login. The 'else' and
 * 'instructeur' variants (P7) get their own chrome and pages under
 * /stal/else and /stal/instructeur. The signed `stal_app` sentence gates
 * the member pages: without it the member sees a friendly closed door
 * (ADR-013), never the pages.
 *
 * Three children are chromeless and run without a member: the login
 * page, a personal-link visit (/stal/:token, sets the cookie and comes
 * back here) and /stal/welkom, which routes a session to its variant.
 */
export const handle = { hideAppChrome: true, getSitemapEntries: () => null }

export const links: Route.LinksFunction = () => [
	{ rel: 'stylesheet', href: oyStalStylesheetUrl },
	// Else's one Vandaag (ADR-036) renders here too
	{ rel: 'stylesheet', href: oyElseDagStylesheetUrl },
]

export const meta: Route.MetaFunction = () => [
	{ name: 'robots', content: 'noindex, nofollow' },
]

const PAGES = new Set([
	'',
	'rooster',
	'lessen',
	'overleg',
	'stal',
	'klussen',
	'fotos',
	'doorgeven',
	'foto-upload',
	'else',
	'instructeur',
])

function sectionOf(pathname: string): string {
	return pathname.replace(/^\/stal\/?/, '').split('/')[0] ?? ''
}

function isBareChild(pathname: string, params: { token?: string }) {
	if (params.token) return true
	const rest = pathname.replace(/^\/stal\/?/, '').split('/')[0] ?? ''
	return rest === 'login' || rest === 'welkom' || !PAGES.has(rest)
}

/** An operator's testweergave: the banner's name (null = a real viewer). */
type Preview = { preview: { name: string } | null }

export type StalLayoutData =
	| { mode: 'bare' }
	| ({ mode: 'closed'; name: string; stableName: string } & Preview)
	| ({ mode: 'member' } & StalShell & Preview)
	| ({ mode: 'else'; shell: ElseShell } & Preview)
	| ({ mode: 'instructeur'; shell: InstructeurShell } & Preview)

export async function loader({ request, params }: Route.LoaderArgs) {
	const url = new URL(request.url)
	if (isBareChild(url.pathname, params as { token?: string })) {
		return data<StalLayoutData>({ mode: 'bare' })
	}
	const now = new Date()
	const viewer = await resolveStalViewer(request, null, now)
	if (!viewer.ok) throw redirect(STAL_LOGIN_PATH)
	const section = sectionOf(url.pathname)
	const preview = viewer.preview ? { name: viewer.name } : null
	if (viewer.variant === 'else') {
		if (section !== 'else') throw redirect(STAL_ELSE_PATH)
		return data<StalLayoutData>({
			mode: 'else',
			shell: await loadElseShell(now),
			preview,
		})
	}
	if (viewer.variant === 'instructeur') {
		if (section !== 'instructeur') throw redirect(STAL_INSTRUCTEUR_PATH)
		return data<StalLayoutData>({
			mode: 'instructeur',
			shell: await loadInstructeurShell(
				{ ...viewer, variant: 'instructeur' },
				now,
			),
			preview,
		})
	}
	if (section === 'else' || section === 'instructeur') throw redirect('/stal')
	const shell = await loadStalShell(
		{ ...viewer, variant: viewer.variant },
		request,
		now,
	)
	if (!shell.consent.stalApp) {
		return data<StalLayoutData>({
			mode: 'closed',
			name: shell.viewer.firstName,
			stableName: shell.stableName,
			preview,
		})
	}
	return data<StalLayoutData>({ mode: 'member', ...shell, preview })
}

export const STAL_ROUTE_ID = 'routes/stal/_layout'

/** The shell data for pages and trays (null on the chromeless children). */
export function useStalShell(): StalShell | null {
	const d = useRouteLoaderData<typeof loader>(STAL_ROUTE_ID)
	return d && d.mode === 'member' ? d : null
}

/** Else's shell (null on every other mode). */
export function useElseShell(): ElseShell | null {
	const d = useRouteLoaderData<typeof loader>(STAL_ROUTE_ID)
	return d && d.mode === 'else' ? d.shell : null
}

/** The instructor's shell (null on every other mode). */
export function useInstructeurShell(): InstructeurShell | null {
	const d = useRouteLoaderData<typeof loader>(STAL_ROUTE_ID)
	return d && d.mode === 'instructeur' ? d.shell : null
}

function tabs(badges: StalShell['badges']): OyTabItem[] {
	return [
		{
			to: '/stal',
			label: 'Vandaag',
			icon: 'ph-sun',
			iconFill: 'ph-sun-fill',
			end: true,
		},
		{
			to: '/stal/rooster',
			label: 'Rooster',
			icon: 'calendar-blank',
			iconFill: 'calendar-blank-fill',
		},
		{
			to: '/stal/lessen',
			label: 'Lessen',
			icon: 'horse',
			iconFill: 'horse-fill',
			dot: badges.lessen,
		},
		{
			to: '/stal/overleg',
			label: 'Overleg',
			icon: 'chat-circle',
			iconFill: 'chat-circle-fill',
			dot: badges.overleg,
		},
		{
			to: '/stal/stal',
			label: 'Stal',
			icon: 'house',
			iconFill: 'house-fill',
		},
	]
}

export function StalHead({
	stableName,
	children,
}: {
	stableName: string
	children?: React.ReactNode
}) {
	return (
		<header className="st-head">
			<Link
				to="/stal"
				className="st-brand"
				aria-label={`${stableName}, Vandaag`}
			>
				<span className="oy-stal-brand" aria-hidden="true">
					🐴
				</span>
				<b>{stableName}</b>
			</Link>
			{children}
		</header>
	)
}

export default function StalLayout({ loaderData }: Route.ComponentProps) {
	if (loaderData.mode === 'bare') {
		return (
			<div className="st-shell">
				<Outlet />
			</div>
		)
	}
	const banner = loaderData.preview ? (
		<OyPreviewBanner name={loaderData.preview.name} />
	) : null
	if (loaderData.mode === 'else') {
		return (
			<OyToastProvider>
				{banner}
				<ElseTrayProvider shell={loaderData.shell}>
					<ElseStalChrome shell={loaderData.shell}>
						<Outlet />
					</ElseStalChrome>
				</ElseTrayProvider>
			</OyToastProvider>
		)
	}
	if (loaderData.mode === 'instructeur') {
		return (
			<OyToastProvider>
				{banner}
				<InstructeurTrayProvider shell={loaderData.shell}>
					<InstructeurChrome shell={loaderData.shell}>
						<Outlet />
					</InstructeurChrome>
				</InstructeurTrayProvider>
			</OyToastProvider>
		)
	}
	if (loaderData.mode === 'closed') {
		return (
			<>
				{banner}
				<div className="st-shell">
					<StalHead stableName={loaderData.stableName} />
					<main className="st-main">
						<StalClosedDoor title={`Dag ${loaderData.name}!`}>
							De Stal-app staat nog niet aan. Hij gaat open zodra het met Else
							en de kring is afgesproken. Je hoort het in de groep.
						</StalClosedDoor>
					</main>
				</div>
			</>
		)
	}
	const shell = loaderData
	const items = tabs(shell.badges)
	return (
		<OyToastProvider>
			<StalTrayProvider shell={shell}>
				{banner}
				<div className="st-shell">
					<StalHead stableName={shell.stableName}>
						<nav aria-label="Hoofdnavigatie" className="st-topnav">
							{items.map((t) => (
								<NavLink key={t.to} to={t.to} end={t.end}>
									{({ isActive }) => (
										<>
											<Icon
												name={isActive && t.iconFill ? t.iconFill : t.icon}
												className="st-topnav-ico"
												aria-hidden="true"
											/>
											{t.label}
											<TabMark item={t} />
										</>
									)}
								</NavLink>
							))}
						</nav>
						<Link
							to="/stal/stal"
							className="oy-app-avatarbtn"
							aria-label={`${shell.viewer.firstName}, De stal`}
						>
							<OyAvatar
								name={shell.viewer.firstName}
								hue={shell.viewer.hue}
								size={34}
							/>
						</Link>
					</StalHead>
					<Outlet />
					<OyTabBar items={items} label="Menu" />
				</div>
			</StalTrayProvider>
		</OyToastProvider>
	)
}
